import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export const getApiBasePath = (): string => {
    return process.env.NEXT_PUBLIC_API_BASE_PATH ?? "admin/v1"
}

export const getApiBaseUrl = (): string => {
    const origin = process.env.NEXT_PUBLIC_API_URL ?? process.env.NEXT_PUBLIC_API_BASE_URL ?? ""
    const basePath = getApiBasePath()
    const trimmedOrigin = origin.replace(/\/+$/, "")
    const trimmedPath = basePath.replace(/^\/+|\/+$/g, "")
    return trimmedPath ? `${trimmedOrigin}/${trimmedPath}` : trimmedOrigin
}

export const getApiUser = (): string => {
    return process.env.NEXT_PUBLIC_API_USER ?? ""
}

export const getAuthHeaderKey = (): string => {
    return process.env.NEXT_PUBLIC_API_AUTH_HEADER_KEY ?? "X-Auth-Token"
}

export const getApiUserHeaderKey = (): string => {
    return process.env.NEXT_PUBLIC_API_USER_HEADER_KEY ?? "x-api-user"
}

export const getAuthTokenKey = (): string => {
    return process.env.NEXT_PUBLIC_AUTH_TOKEN_KEY ?? "dompetAmanAuthToken"
}

const getAuthToken = (): string | null => {
    if (typeof window === 'undefined') return null
    try {
        return localStorage.getItem(getAuthTokenKey())
    } catch {
        return null
    }
}

export const api = createApi({
    reducerPath: 'api',
    baseQuery: (() => {
        const rawBaseQuery = fetchBaseQuery({
            baseUrl: getApiBaseUrl(),
            prepareHeaders: (headers) => {
                const apiUser = getApiUser()
                const apiUserHeader = getApiUserHeaderKey()
                if (apiUser && apiUserHeader) {
                    headers.set(apiUserHeader, apiUser)
                }
                // Attach auth token header if available
                const token = getAuthToken()
                if (token) {
                    headers.set(getAuthHeaderKey(), token)
                }
                return headers
            },
        })

        const baseQueryWithAuth = async (args: any, api: any, extraOptions: any) => {
            const result: any = await rawBaseQuery(args, api, extraOptions)
            const error = result?.error
            const isUnauthorized =
                error?.status === 401 ||
                error?.data?.code === 401 ||
                (typeof error?.data?.message === 'string' &&
                    error.data.message.toLowerCase().includes('token expired'))

            if (isUnauthorized && typeof window !== 'undefined') {
                // Notify user before forcing logout
                try {
                    // Simple notification; replace with a toasting system if available
                    window.alert('Your session has expired. Please log in again.')
                } catch {
                    // ignore
                }
                try {
                    // Clear auth artifacts
                    const tokenKey = getAuthTokenKey()
                    if (tokenKey) {
                        localStorage.removeItem(tokenKey)
                    }
                    localStorage.removeItem('dompetAmanUser')
                } catch {
                    // ignore cleanup errors
                }
                // Redirect to login
                try {
                    window.location.replace('/')
                } catch {
                    // fallback
                    window.location.href = '/'
                }
            }

            return result
        }

        return baseQueryWithAuth as any
    })(),
    tagTypes: ['Dashboard', 'User', 'Transactions'],
    endpoints: (builder) => ({
        // Auth
        login: builder.mutation<{ authToken: string }, { email: string; password: string }>({
            query: (credentials) => ({
                url: 'auth/login',
                method: 'POST',
                body: credentials,
            }),
            async onQueryStarted(_arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    if (typeof window !== 'undefined' && data?.authToken) {
                        localStorage.setItem(getAuthTokenKey(), data.authToken)
                    }
                } catch {
                    // ignore
                }
            },
        }),

        // Dashboard
        getDashboardSummary: builder.query<unknown, void>({
            query: () => ({
                url: 'dashboard/summary',
                method: 'GET',
            }),
            providesTags: ['Dashboard'],
        }),

        // Users
        searchUser: builder.query<unknown, { searchText: string }>({
            query: ({ searchText }) => ({
                url: 'searchuser',
                method: 'GET',
                params: { searchText },
            }),
            providesTags: ['User'],
        }),
        getUserDetails: builder.query<unknown, string>({
            query: (userId) => ({
                url: `users/${userId}`,
                method: 'GET',
            }),
            providesTags: (_result, _err, id) => [{ type: 'User', id }],
        }),

        // Transactions
        getTransactions: builder.query<
            unknown,
            {
                status?: string
                startDate?: string
                endDate?: string
                searchText?: string
                page?: number
                pageSize?: number
            }
        >({
            query: (params) => ({
                url: 'transactions',
                method: 'GET',
                params,
            }),
            providesTags: ['Transactions'],
        }),
        getTransactionDetail: builder.query<unknown, string>({
            query: (transactionId) => ({
                url: `transactions/${transactionId}`,
                method: 'GET',
            }),
            providesTags: (_res, _err, id) => [{ type: 'Transactions', id }],
        }),
        updateTransactionStatus: builder.mutation<
            unknown,
            { id: string; status: 'APPROVED' | 'SUCCESS' | 'REJECTED' | string }
        >({
            query: ({ id, status }) => ({
                url: `transactions/${id}/status`,
                method: 'POST',
                body: { status },
            }),
            invalidatesTags: (_res, _err, { id }) => [
                'Transactions',
                { type: 'Transactions', id },
                'Dashboard',
            ],
        }),
        updateCapillaryCustomer: builder.mutation<
            unknown,
            { mobile: string; fields: Record<string, string> }
        >({
            query: ({ mobile, fields }) => ({
                url: `capillary/update-customer`,
                method: 'PUT',
                params: {
                    source: 'INSTORE',
                    identifierName: 'mobile',
                    identifierValue: mobile,
                },
                body: {
                    profiles: [
                        {
                            fields,
                            identifiers: [{ type: 'mobile', value: mobile }],
                            source: 'INSTORE',
                            accountId: '-1',
                        },
                    ],
                },
            }),
        }),
        exportTransactions: builder.query<
            Blob,
            | {
                status?: string
                startDate?: string
                endDate?: string
                searchText?: string
                page?: number
                pageSize?: number
            }
            | undefined
        >({
            query: (params) => ({
                url: 'transactions/export',
                method: 'GET',
                params: params ?? undefined,
                responseHandler: (response: Response) => response.blob(),
            }),
            providesTags: ['Transactions'],
        }),
    }),
})

export const {
    useLoginMutation,
    useGetDashboardSummaryQuery,
    useSearchUserQuery,
    useGetUserDetailsQuery,
    useGetTransactionsQuery,
    useGetTransactionDetailQuery,
    useUpdateTransactionStatusMutation,
    useUpdateCapillaryCustomerMutation,
    useExportTransactionsQuery,
} = api

